import * as React from 'react';
import get from 'lodash/get';
import { ComponentProps, ComplexValue } from 'ui';
import Highcharts from 'highcharts';
import ResizeSensor from 'resize-sensor';

import 'highcharts/modules/heatmap';
Highcharts.AST.allowedAttributes.push('data-value');

import { ReplaceDataLabelsWithDot } from 'app/components/risk-matrix/HighChartsExtension';
ReplaceDataLabelsWithDot(Highcharts);

import styles from './risk-matrix.scss';
import { deepEquals } from 'app/infrastructure/js';
import { numericColorBrightness, numericColorToRgb } from 'app/infrastructure/universal/color';
import { RiskMatrixDefinition, RiskMatrixCell, RiskMatrixAxis } from 'app/area/case/types-store';
import { isFirefox } from 'app/infrastructure/platform';
import { calculateHeight } from 'app/components/risk-matrix/calculate-height';
import { ValidationLevel, FlexValidation } from 'ui/common';
import debounce from 'lodash/debounce';

import { delayForDisplay } from 'app/components/risk-matrix/highcharts-tooltip-delay';
import { DEBOUNCE_TIME_DEFAULT, DEBOUNCE_TIME_SCROLL } from 'ui/constants';
import { RiskMatrixTypeResult } from 'app/area/case-report/risk-listing/types-store';
import isNumber from 'lodash/isnumber';
import merge from 'lodash/merge';
import { setRiskReportOptionsSeries } from 'app/area/case-report/risk-listing/risk-matrix-helpers';
import { FavouriteLayoutAttributes } from 'app/components/favourite-layout/types';
import { RiskMatrixCellProperties } from 'app/area/case-report/risk-listing/constants';
import { riskReportAttributeMappings } from 'app/area/reports/containers/chart/components/attributes/constants';
import { flatToNestedObject } from 'app/components/risk-matrix/flat-to-nested-object';

delayForDisplay(Highcharts);

export enum AxisLabelMode {
  all = 'all',
  selected = 'selected'
}

export interface RiskMatrixProps extends ComponentProps {
  readOnly: boolean;
  hideRiskWeight?: boolean;
  xAxisLabelMode: AxisLabelMode;
  yAxisLabelMode: AxisLabelMode;
  riskMatrixDefinition: RiskMatrixDefinition;
  onSelectedXChanged: (x: number, axisCodetableId: number) => void;
  onSelectedYChanged: (y: number, axisCodetableId: number) => void;
  /**
   * Values per axis.
   * Assumed to follow the same order as cell values in definition.
   */
  preselectedValues: number[];
  optionToCellValue: (option: ComplexValue<number>) => number;
  textXAxisTitle?: string;
  textYAxisTitle?: string;
  displayAllValues?: boolean;
  onSelectedCell?: OnSelectedCell;
  rowHeight?: number;
  riskMatrixTypeResult?: RiskMatrixTypeResult;
  locale?: string;
  title?: string;
  layoutAttributes?: FavouriteLayoutAttributes;
  height?: number;
  showAxisTitles?: boolean;
  useSpacing?: boolean;
  onCaseNumberModalOpen?: (caseNumbers: number[], modalTitle: string, totalCases: number) => void;
}

interface HighChartsSeriesExtended extends Highcharts.SeriesHeatmapOptions {
  hideLabels?: boolean;
}

interface HighChartsOptionsExtended extends Highcharts.Options {
  series: HighChartsSeriesExtended[];
}

export type OnSelectedCell = (x: number, y: number) => void;

export class RiskMatrix extends React.Component<RiskMatrixProps, {}> {

  private chartElement: HTMLElement;
  private chart: Highcharts.Chart;
  private initialRender = true;

  private createHighchartsOptions(preselectedValues: number[], title?: string): Highcharts.Options {
    const axes = this.props.riskMatrixDefinition?.axes;
    const noOfAxes = axes?.filter(axis => !axis.fakeAxis).length;
    const oneAxesStyle = noOfAxes === 1 && styles.tooltipWrapperOneAxes;
    const twoAxesStyle = noOfAxes === 2 && styles.tooltipWrapperTwoAxes;
    const zAxis = axes?.[2] && axes[2];
    const zAxisPreselectedValues = preselectedValues?.[2];

    return {
      title: { text: title || '', style: { fontSize: '15px', fontWeight: 'bold' }, margin: 5, align: 'left' },
      chart: {
        type: 'heatmap',
        style: {
          fontFamily: `"Gantari", "Noto Sans", "Noto Sans Arabic", "Noto Sans JP", "Noto Sans SC", 
          "Noto Sans TC", "Noto Sans KR", "Noto Sans Thai", "Arial", "sans-serif"`
        },
        height: this.props.height || this.calculateHeight(),
        animation: false
      },
      tooltip: {
        enabled: true,
        outside: true,
        formatter: function () { // NB: Do not change to arrow. 'this' is the point object when used as formatter.
          return `<div class="${styles.tooltipWrapper} ${oneAxesStyle} ${twoAxesStyle}">
            <div class="${styles.title}">
              ${this.series.xAxis.options.title.text}
              <div class="${styles.value}">
                ${this.series.xAxis.categories[this.x]}
              </div>
            </div>
            <div class="${styles.title}">
              ${this.series.yAxis.options.title.text}
              <div class="${styles.value}">
                ${this.series.yAxis.categories[this.y]}
              </div>
            </div>
            <div class="${styles.title}">
              ${zAxis ? zAxis.data?.description : ''}
              <div class="${styles.value}">
                ${zAxis ? zAxis.data?.options.filter(option => option.value === zAxisPreselectedValues)?.[0]?.description : ''}
              </div>
            </div>
          </div>`;
        },
        useHTML: true,
        shadow: false,
        delayForDisplay: 500,
        hideDelay: 0,
        backgroundColor: 'rgba(0,0,0,0)',
        borderColor: 'rgba(0,0,0,0)',
        style: { color: 'rgba(0,0,0,0)' }
      },
      legend: { enabled: false },
      credits: { enabled: false },
      exporting: { enabled: false },
      xAxis: {
        labels: {
          distance: 8
        }
      },
      yAxis: {
        labels: {
          distance: 15
        }
      }
    };
  }

  public shouldComponentUpdate(newProps: RiskMatrixProps) {
    let updateOptions = false;
    const { riskMatrixTypeResult } = this.props;

    if (
      !this.chartElement || !this.props.riskMatrixDefinition || this.props.riskMatrixDefinition.cells.length === 0 ||
      !!this.getAxesWaitingForExternalValueSelect(this.props.riskMatrixDefinition, this.props.preselectedValues) ||
      !!this.getAxesWaitingForExternalValueSelect(newProps.riskMatrixDefinition, newProps.preselectedValues) ||
      !deepEquals(this.props.validationDescendants, newProps.validationDescendants) ||
      this.props.height !== newProps.height ||
      this.props.rowHeight !== newProps.rowHeight
    ) {
      return true;
    }

    if (
      !deepEquals(this.props.riskMatrixDefinition, newProps.riskMatrixDefinition) ||
      !deepEquals(this.props.layoutAttributes, newProps.layoutAttributes) ||
      !!riskMatrixTypeResult && !deepEquals(riskMatrixTypeResult, newProps.riskMatrixTypeResult) ||
      this.props.title !== newProps.title
    ) {
      updateOptions = true;
    }

    if (!updateOptions && !deepEquals(this.props.preselectedValues, newProps.preselectedValues)) {
      updateOptions = true;
      // It should be possible to use the chart API to update this without recreating the chart
      // but this means a lot more complexity. It might possibly improve performance however.
    }

    if (updateOptions) {
      const options = this.createHeatmapOptionsFromDefinition(newProps.riskMatrixDefinition, newProps.preselectedValues,
        newProps.displayAllValues, newProps.onSelectedCell, newProps.riskMatrixTypeResult, newProps.layoutAttributes, newProps.title);
      this.renderChart(options);
    } else if (this.chart) {
      this.chart.reflow();
    }

    return false;
  }

  private onScroll: () => void = debounce(() => {
    this.chart?.update({
      tooltip: {
        enabled: false
      }
    });
  }, DEBOUNCE_TIME_SCROLL);

  public componentDidMount() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  public componentWillUnmount() {
    //Destroy chart before unmount.
    this.initialRender = true;
    this.destroy();
    window.removeEventListener('scroll', this.onScroll);
  }

  private onChartElementReady = (element: HTMLElement) => {
    //If a render has happened, this might be null for older callbacks.
    if (element) {
      this.chartElement = element;
      this.forceHighchartsLightMode(this.chartElement);
      // eslint:disable-next-line:no-unused-expression
      new ResizeSensor(element.parentElement, () => this.reflowChart());
      this.renderChart(this.createHeatmapOptionsFromDefinition(this.props.riskMatrixDefinition, this.props.preselectedValues,
        this.props.displayAllValues, this.props.onSelectedCell, this.props.riskMatrixTypeResult, this.props.layoutAttributes,
        this.props.title));
    }
  };

  private renderChart(options: Highcharts.Options) {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
    this.forceHighchartsLightMode(this.chartElement);
    Highcharts.chart(this.chartElement, options, (createdChart: Highcharts.Chart) => {
      this.chart = createdChart;    });
  }
  private forceHighchartsLightMode(element: HTMLElement) {
    if (!element) {
      return;
    }
    element.classList.remove('highcharts-dark');
    element.classList.add('highcharts-light');
  }

  private reflowChart: () => void = debounce(() => {
    if (this.chart) {
      this.chart.reflow();
    }
  }, DEBOUNCE_TIME_DEFAULT);

  private createHeatmapOptionsFromDefinition(definition: RiskMatrixDefinition, preselectedValues: number[],
    displayAllValues?: boolean, onSelectedCell?: OnSelectedCell, riskMatrixTypeResult?: RiskMatrixTypeResult,
    layoutAttributes?: FavouriteLayoutAttributes, title?: string) {
    const options = this.createHighchartsOptions(preselectedValues, title);

    if (definition && preselectedValues) {
      this.setOptions(options, definition, preselectedValues, displayAllValues, onSelectedCell, riskMatrixTypeResult, layoutAttributes);
    }

    return options;
  }

  private setAxisOptions(options: Highcharts.Options, definition: RiskMatrixDefinition, preselectedValues: number[]) {

    const axisLabelsOptions = (axis: RiskMatrixAxis, axisIndex: number, axisLabelMode: AxisLabelMode) => ({
      formatter: function () {
        if (axisLabelMode === AxisLabelMode.all) {
          return `${this.value}`;
        }
        else if (axisLabelMode === AxisLabelMode.selected) {
          if (preselectedValues[axisIndex] === axis.data.options[this.pos].value) {
            return `${this.value}`;
          }
          else {
            return '';
          }
        }

        return '';
      },
      style: {
        fontSize: '15px'
      }
    });

    const axisTitleOptions = (title: string) => ({
      text: title
    });

    const { textXAxisTitle, textYAxisTitle } = this.props;
    const xAxis = this.getAxis(definition, 0);
    const xAxisTitleFromDefinition = axisTitleOptions(xAxis.data.description).text;
    options.xAxis = {
      title: textXAxisTitle ? axisTitleOptions(textXAxisTitle) : xAxis ? axisTitleOptions(xAxisTitleFromDefinition) : null,
      lineWidth: 0,
      gridLineWidth: 0,
      tickWidth: 0,
      borderWidth: 0,
      shadow: false,
      categories: this.getAxis(definition, 0).data.options.map(o => o.description),
      labels: {
        ...(options.xAxis as Highcharts.XAxisOptions).labels,
        ...axisLabelsOptions(this.getAxis(definition, 0), this.getAxisIndex(definition, 0), this.props.xAxisLabelMode)
      }
    } as Highcharts.XAxisOptions;

    const yAxis = this.getAxis(definition, 1);
    const yAxisTitleFromDefinition = axisTitleOptions(yAxis.data.description).text;
    options.yAxis = {
      title: textYAxisTitle ? axisTitleOptions(textYAxisTitle) : yAxis ? axisTitleOptions(yAxisTitleFromDefinition) : null,
      lineWidth: 0,
      gridLineWidth: 0,
      tickWidth: 0,
      borderWidth: 0,
      shadow: false,
      categories: this.getAxis(definition, 1).data.options.map(o => o.description),
      ...(options.yAxis as Highcharts.YAxisOptions).labels,
      labels: axisLabelsOptions(this.getAxis(definition, 1), this.getAxisIndex(definition, 1), this.props.yAxisLabelMode)
    } as Highcharts.YAxisOptions;
  }

  private setSeriesOptions(
    options: Highcharts.Options, definition: RiskMatrixDefinition, preselectedValues: number[], onSelectedCell?: OnSelectedCell
  ) {
    const xIndex = this.getAxisIndex(definition, 0);
    const yIndex = this.getAxisIndex(definition, 1);
    const xAxis = this.getAxis(definition, 0);
    const yAxis = this.getAxis(definition, 1);

    const setConsequenceValues = (x: number, y: number) => {
      if (xAxis.editable && (yAxis.editable || yAxis.data.options[y].value === preselectedValues[yIndex])) {
        this.props.onSelectedXChanged(xAxis.data.options[x].value, xAxis.data.codetableId);
      }
      if (yAxis.editable && (xAxis.editable || xAxis.data.options[x].value === preselectedValues[xIndex])) {
        this.props.onSelectedYChanged(yAxis.data.options[y].value, yAxis.data.codetableId);
      }
      return;
    };

    options.series = [{
      borderWidth: 2,
      borderColor: '#FFF',
      point: {
        events: {
          click: !this.props.readOnly && ((event: Highcharts.SeriesClickEventObject) => {
            (onSelectedCell || setConsequenceValues)(event.point.x, event.point.y);
            this.chart.update({
              tooltip: {
                enabled: false
              }
            });
            return false;
          })
        }
      },
      states: {
        hover: !this.props.readOnly && {
          brightness: 0.4
        }
      },
      type: 'heatmap'
    } as Highcharts.SeriesHeatmapOptions];
  }

  private setSeriesData(
    options: HighChartsOptionsExtended, definition: RiskMatrixDefinition,
    preselectedValues: number[], displayAllValues?: boolean
  ) {
    const xIndex = this.getAxisIndex(definition, 0);
    const yIndex = this.getAxisIndex(definition, 1);

    const columnCount = get(options, 'xAxis.categories.length');
    let labelOffset = !!columnCount && columnCount < 7 ? 0 : -1;
    if (this.initialRender) {
      labelOffset -= 1;
    }

    options.series[0].hideLabels = this.props.hideRiskWeight;
    options.series[0].data = definition.cells
      .filter( // Filter to only show those that are selectable axes in matrix or match the preselected values.
        c => c.axisValues.reduce(
          (acc, v, i) => (acc && (i === xIndex || i === yIndex || v.value === preselectedValues[i])), true)
      )
      .map(
        c => {
          const getAxisCellValue = (cellDataValue: ComplexValue<number>) => {
            return isNumber(cellDataValue) ? cellDataValue
              : !!cellDataValue ? cellDataValue.value : 0;
          };
          const readOnlyCell = this.cellReadOnly(c, definition, preselectedValues);
          const inactiveCell = c.inactive;
          const cellColor = c && c.data.riskArea.backgroundColor;
          const cellRgb = numericColorToRgb(cellColor);
          const cellColorBrightness = numericColorBrightness(cellColor);
          const preselectedCell = preselectedValues &&
            getAxisCellValue(c.axisValues[xIndex]) === preselectedValues[xIndex] &&
            getAxisCellValue(c.axisValues[yIndex]) === preselectedValues[yIndex];
          const blackText = cellColorBrightness > 125;

          /**
           * Detect firefox to select htmlRendering of numbers.
           * Numbers get missplaced in firefox when using svg rendering.
           */
          const isFF = isFirefox();
          const bgColor = readOnlyCell || inactiveCell ?
            `rgba(${cellRgb.r},${cellRgb.g},${cellRgb.b},0.5)` :
            `rgba(${cellRgb.r},${cellRgb.g},${cellRgb.b},1)`;

          return {
            x: definition.axes[xIndex].data.options.findIndex(v => v.value === getAxisCellValue(c.axisValues[xIndex])),
            y: definition.axes[yIndex].data.options.findIndex(v => v.value === getAxisCellValue(c.axisValues[yIndex])),
            color: bgColor,
            borderColor: '#FFF',
            borderWidth: 2,
            cursor: readOnlyCell ? 'default' : 'pointer',
            shadow: false,
            rotation: 0,
            selected: preselectedCell,
            marker: {
              states: {
                select: {
                  color: bgColor,
                  lineColor: '#FFF',
                  lineWidth: 2
                }
              }
            },
            events: {
              mouseOver: (event: Event) => {
                // TODO: Hovering over a point that is not selected should show risk weight text.
                event.preventDefault();
                this.chart.update({
                  tooltip: {
                    enabled: true
                  }
                });
                return false;
              }
            },
            dataLabels: {
              enabled: true,
              color: blackText ? '#000' : '#FFF',
              style: {
                fontSize: '16px',
                textOutline: blackText ? '' : '.5px rgba(0,0,0,.25)',
                textShadow: blackText ? '' : '0 0 16px rgba(0, 0, 0, .5)'
              },
              formatter: function () {
                if (readOnlyCell) {
                  return undefined;
                }
                else if (preselectedCell || displayAllValues) {
                  return !isFF ?
                    String(c.data.weight)
                    :
                    `<span class="content ${blackText ? 'blacktext' : ''}">${String(c.data.weight)}</span>`;
                }
                return undefined;
              },
              align: 'center',
              useHTML: isFF,
              padding: 0,
              x: labelOffset
            },
            value: c.data.weight
          } as any;
        });
    this.initialRender = false;
    if (options.series[0].data.length === 0) {
      return; // TODO: Make emtpy pretty matrix.
    }
  }

  private cellReadOnly(cell: RiskMatrixCell, definition: RiskMatrixDefinition, preselectedValues: number[]) {
    if (!preselectedValues || !cell) {
      return false;
    }

    const xAxis = this.getAxis(definition, 0);
    const xIndex = this.getAxisIndex(definition, 0);
    const yAxis = this.getAxis(definition, 1);
    const yIndex = this.getAxisIndex(definition, 1);

    const readOnlyX = xAxis.editable || preselectedValues[xIndex] === undefined ?
      false : preselectedValues[xIndex] !== cell.axisValues[xIndex].value;
    const readOnlyY = yAxis.editable || preselectedValues[yIndex] === undefined ?
      false : preselectedValues[yIndex] !== cell.axisValues[yIndex].value;

    return readOnlyX || readOnlyY;
  }

  private setOptions(options: Highcharts.Options, definition: RiskMatrixDefinition, preselectedValues: number[],
    displayAllValues?: boolean, onSelectedCell?: OnSelectedCell, riskMatrixTypeResult?: RiskMatrixTypeResult,
    layoutAttributes?: FavouriteLayoutAttributes) {

    if (!definition || !definition.axes || !definition.cells) {
      return;
    }

    this.setAxisOptions(options, definition, preselectedValues);
    this.setSeriesOptions(options, definition, preselectedValues, onSelectedCell);
    this.setSeriesData(options as HighChartsOptionsExtended, definition, preselectedValues, displayAllValues);
    if (!!riskMatrixTypeResult) {
      this.setRiskReportOptions(options, riskMatrixTypeResult, layoutAttributes, definition);
    }
  }

  private mergeStaticConfig(options: Highcharts.Options, layoutAttributes: FavouriteLayoutAttributes): Highcharts.Options {
    const mappedLayoutAttributes = flatToNestedObject(layoutAttributes);
    return merge(options, mappedLayoutAttributes.Highcharts || {});
  }

  private setAxis(options: Highcharts.Options, layoutAttributes: FavouriteLayoutAttributes, axisName: string) {
    options[axisName].labels.style.fontSize = '11px';
    options[axisName].labels.enabled = layoutAttributes?.['Highcharts' + axisName + 'labels.enabled'] !== false;
    options[axisName].title.enabled = !layoutAttributes?.['columnTitles.axes.disableTitle'];
  }

  private setRiskReportOptions(options: Highcharts.Options, riskMatrixTypeResult: RiskMatrixTypeResult,
    layoutAttributes: FavouriteLayoutAttributes, definition: RiskMatrixDefinition) {
    this.setAxis(options, layoutAttributes, 'xAxis');
    this.setAxis(options, layoutAttributes, 'yAxis');
    options = this.mergeStaticConfig(options, layoutAttributes);
    options.plotOptions = { heatmap: { cursor: 'pointer' } };
    options.chart.spacingRight = this.props.useSpacing ? options.chart.spacingRight : RiskMatrixCellProperties.DefaultChartSpacingRight;
    options.chart.height = this.calculateHeight();

    const onOpenModal = (xAxisItemIndex: number, yAxisItemIndex: number) => {
      const resultData = riskMatrixTypeResult?.cells['x' + xAxisItemIndex + 'y' + yAxisItemIndex].data;
      const { cases: caseNumbers, count: totalCases } = resultData;
      const [xAxis, yAxis] = definition.axes;
      const yAxisTitle = !!yAxis?.fakeAxis ? '' :
        `, ${yAxis.data.description}: ${(options.yAxis as Highcharts.XAxisOptions)?.categories[yAxisItemIndex]}`;
      const modalTitle = `${xAxis.data.description}:
        ${(options.xAxis as Highcharts.XAxisOptions)?.categories[xAxisItemIndex]}${yAxisTitle}`;
      this.props.onCaseNumberModalOpen(caseNumbers, modalTitle, totalCases);
    };

    (options.series[0] as Highcharts.SeriesOptionsRegistry['SeriesHeatmapOptions']) = setRiskReportOptionsSeries(
      (options.series[0] as Highcharts.SeriesOptionsRegistry['SeriesHeatmapOptions']),
      layoutAttributes,
      riskMatrixTypeResult,
      this.props.locale,
      this.props.onSelectedCell,
      onOpenModal
    );
  }

  private getAxis(definition: RiskMatrixDefinition, axisId: number) {
    return definition.axes[definition.axes.findIndex(a => a.id === axisId)];
  }

  private getAxisIndex(definition: RiskMatrixDefinition, axisId: number) {
    return definition.axes.findIndex(a => a.id === axisId);
  }

  private waitForAxisValueSelect(definition: RiskMatrixDefinition, axisId: number, preselectedValues: number[]) {
    const axis = definition && this.getAxis(definition, axisId);
    const axisIndex = definition && axis && this.getAxisIndex(definition, axis.id);

    return axis && !axis.editable && axisIndex >= 0 && preselectedValues[axisIndex] <= 0;
  }

  public render() {
    return this.renderInvalidConfig() || this.renderWaitingForValue() || this.renderMatrixElement();
  }

  private invalidDefinition() {
    const { riskMatrixDefinition } = this.props;

    return !riskMatrixDefinition || riskMatrixDefinition.cells.length === 0;
  }

  private renderInvalidConfig() {
    const { matrixContainer, infoText } = styles;

    if (!this.invalidDefinition()) {
      return undefined;
    }

    return (
      <div className={matrixContainer}>
        <p className={infoText}>Cannot render matrix. Invalid configuration.</p>
      </div>
    );
  }

  private getAxesWaitingForExternalValueSelect(definition: RiskMatrixDefinition, preselectedValues: number[]) {

    const waitForXValueSelect = this.waitForAxisValueSelect(definition, 0, preselectedValues);
    const waitForYValueSelect = this.waitForAxisValueSelect(definition, 1, preselectedValues);
    const waitForZValueSelect = this.waitForAxisValueSelect(definition, 2, preselectedValues);

    if (!waitForXValueSelect && !waitForYValueSelect && !waitForZValueSelect) {
      return undefined;
    }

    return {
      x: waitForXValueSelect,
      y: waitForYValueSelect,
      z: waitForZValueSelect
    };
  }

  private hasChildErrors() {
    const { validationDescendants } = this.props;
    return validationDescendants && !!validationDescendants.length && validationDescendants.some(v => v.level === ValidationLevel.error);
  }

  private hasChildWarnings() {
    const { validationDescendants } = this.props;
    return validationDescendants && !!validationDescendants.length && validationDescendants.some(v => v.level === ValidationLevel.warning);
  }

  private getErrors = () => {
    return this.props.validationDescendants && this.props.validationDescendants
      .filter(v => v.level === ValidationLevel.error).map((v, i) => <FlexValidation key={i}{...v} />);
  };

  private getWarnings = () => {
    return this.props.validationDescendants && this.props.validationDescendants
      .filter(v => v.level === ValidationLevel.warning).map((v, i) => <FlexValidation key={i}{...v} />);
  };

  private getRiskMatrixValidationStyle = () => {
    return this.hasChildErrors() ? styles.hasChildErrors : (this.hasChildWarnings() ? styles.hasChildWarnings : '');
  };

  private renderWaitingForValue() {

    const { riskMatrixDefinition, preselectedValues, validationDescendants } = this.props;
    const { matrixContainer, infoText } = styles;

    const axesWaitingForSelect = this.getAxesWaitingForExternalValueSelect(riskMatrixDefinition, preselectedValues);

    if (!axesWaitingForSelect) {
      return undefined;
    }

    const waitForXValueDescription = axesWaitingForSelect.x && this.getAxis(riskMatrixDefinition, 0).data.pleaseSelectDescription;
    const waitForYValueDescription = axesWaitingForSelect.y && this.getAxis(riskMatrixDefinition, 1).data.pleaseSelectDescription;
    const waitForZValueDescription = axesWaitingForSelect.z && this.getAxis(riskMatrixDefinition, 2).data.pleaseSelectDescription;
    const errors = validationDescendants &&
      validationDescendants.filter(v => v.level === ValidationLevel.error).map((v, i) => <FlexValidation key={i} {...v} />);
    const warnings = validationDescendants &&
      validationDescendants.filter(v => v.level === ValidationLevel.warning).map((v, i) => <FlexValidation key={i} {...v} />);

    return (
      <div className={`${matrixContainer} ${this.getRiskMatrixValidationStyle()}`}>
        {axesWaitingForSelect.x && !axesWaitingForSelect.y && !axesWaitingForSelect.z &&
          <p className={infoText}>{waitForXValueDescription}</p>}
        {axesWaitingForSelect.y && !axesWaitingForSelect.z &&
          <p className={infoText}>{waitForYValueDescription}</p>}
        {axesWaitingForSelect.z &&
          <p className={infoText}>{waitForZValueDescription}</p>}
        {this.hasChildErrors() && errors}
        {this.hasChildWarnings() && warnings}
      </div>
    );
  }

  private calculateHeight() {
    const { riskMatrixDefinition, rowHeight, layoutAttributes } = this.props;
    const yAxis = riskMatrixDefinition && this.getAxis(riskMatrixDefinition, 1);
    const rowCount = get(yAxis, 'data.options.length');
    const heightFromProps = this.props.height;
    const calculatedHeight = calculateHeight(rowCount, rowHeight);
    const isShowCaseNumbersEnabled = !!layoutAttributes?.[riskReportAttributeMappings.showCaseNumbers];
    
    // The height from props should be overriden when 'show case numbers' attribute is on to preserve the minimum cell row height.
    return heightFromProps ?
      isShowCaseNumbersEnabled ?
        Math.max(heightFromProps, calculatedHeight) :
        heightFromProps :
      calculatedHeight;
  }

  private renderMatrixElement() {
    const { matrixContainer } = styles;
    const height = this.calculateHeight();
    const heightStyle = !!height ? `${height}px` : undefined;

    return (
      <div className={`${matrixContainer} ${this.getRiskMatrixValidationStyle()}`}>
        <div style={{ height: heightStyle }} ref={this.onChartElementReady} />
        {this.props.children}
        {this.hasChildErrors() && this.getErrors()}
        {this.hasChildWarnings() && this.getWarnings()}
      </div>
    );
  }

  public destroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }

    if (this.chartElement && this.chartElement.parentElement) {
      ResizeSensor.detach(this.chartElement.parentElement);
    }
    this.chartElement = null;
  }
}
